import { ErrorState } from '@/components/feedback/error-state';
import { LoadingState } from '@/components/feedback/loading-state';
import React from 'react';
import { TabScreenWrapper } from './tab-screen-wrapper';

interface QueryStateProps {
  isLoading: boolean;
  error: Error | string | null;
  onRetry?: () => void;
  loadingMessage?: string;
  errorMessage?: string;
  children: React.ReactNode;
  fullScreen?: boolean;
}

/**
 * Wrapper component that handles loading and error states for data queries.
 * Uses LoadingState and ErrorState components consistently.
 */
export function QueryState({
  isLoading,
  error,
  onRetry,
  loadingMessage = 'Loading...',
  errorMessage,
  children,
  fullScreen = true,
}: QueryStateProps) {
  if (isLoading) {
    return <LoadingState message={loadingMessage} fullScreen={fullScreen} />;
  }

  if (error) {
    const errorText = errorMessage || (error instanceof Error ? error.message : String(error));
    return (
      <ErrorState
        error={errorText}
        onRetry={onRetry}
        fullScreen={fullScreen}
      />
    );
  }

  return <>{children}</>;
}

/**
 * QueryState wrapper specifically for tab screens (uses TabScreenWrapper)
 */
export function TabQueryState({
  isLoading,
  error,
  onRetry,
  loadingMessage,
  errorMessage,
  children,
}: Omit<QueryStateProps, 'fullScreen'>) {
  if (isLoading) {
    return (
      <TabScreenWrapper>
        <LoadingState message={loadingMessage} fullScreen={false} />
      </TabScreenWrapper>
    );
  }

  if (error) {
    const errorText = errorMessage || (error instanceof Error ? error.message : String(error));
    return (
      <TabScreenWrapper>
        <ErrorState
          error={errorText}
          onRetry={onRetry}
          fullScreen={false}
        />
      </TabScreenWrapper>
    );
  }

  return <>{children}</>;
}
